/*
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
 */
import { defineStore } from "pinia";

/**
 * useMenuStore is a Pinia store definition for managing a menu with the ability to handle favorites.
 *
 * The store contains state, actions, and functionality to add or remove favorite items within a specified menu.
 *
 * @constant
 * @type {object}
 * @property {Array} menu - An array representing the menu structure.
 *
 * Actions:
 * - `addFavorite(item, targetMenu)`:
 *   Adds an item to the specified menu as a favorite. Displays a warning if the item is undefined.
 *
 * - `removeFavorite(item, targetMenu)`:
 *   Removes an item from the favorites in the specified menu. Displays a warning if the item is undefined.
 *
 * Warning messages are triggered when attempting to add or remove undefined items.
 */
export const useMenuStore = defineStore({
  id: "MenuStore",
  state: () => ({
    menu: [],
  }),
  actions: {
    addFavorite(item, targetMenu) {
      if (!item) {
        console.warn("addFavorite, item is undefined");
        return;
      }

      const menuElement = this.menu.find(
        (element) => element.key === targetMenu,
      );
      if (!menuElement) {
        // Menu not initialized (app uses custom menu store)
        return;
      }
      menuElement.items.push(item);
    },
    removeFavorite(item, targetMenu) {
      if (!item) {
        console.warn("removeFavorite, item is undefined");
        return;
      }

      const menuElement = this.menu.find(
        (element) => element.key === targetMenu,
      );
      if (!menuElement) {
        // Menu not initialized (app uses custom menu store)
        return;
      }

      let index = menuElement.items.findIndex((targetMenuItem) =>
        "FolderId" in targetMenuItem.to.params
          ? targetMenuItem.to.params.FolderId === item.to.params.FolderId
          : targetMenuItem.to.params.id === item.to.params.id,
      );
      if (index !== -1) {
        menuElement.items.splice(index, 1);
      }
    },
  },
});
